import axios from 'axios';
import type { AxiosHeaders, AxiosRequestConfig, AxiosResponse } from 'axios';



import { useUserStore } from '@/store/useUserStore';
import { generateInternalSSOUrl, isInternalDomain } from '@/utils/const';


declare const AXIOS_BASE: string;
declare const AXIOS_HEADERS: string;

export type RequestConfig<TData = unknown> = {
  url?: string;
  method: 'GET' | 'PUT' | 'PATCH' | 'POST' | 'DELETE' | 'OPTIONS';
  params?: unknown;
  data?: TData | FormData;
  responseType?: 'arraybuffer' | 'blob' | 'document' | 'json' | 'text' | 'stream';
  signal?: AbortSignal;
  headers?: AxiosRequestConfig['headers'];
};

export type ResponseConfig<TData = unknown> = {
  data: TData;
  status: number;
  statusText: string;
  headers?: AxiosResponse['headers'];
};

export type ResponseErrorConfig<TError = unknown> = TError;

/**
 * 从cookie中获取认证token
 * @returns 认证token，如果不存在则返回null
 */
export const getAuthTokenFromCookie = (): string | null => {
  // 在浏览器环境中获取cookie
  if (typeof document !== 'undefined') {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'ide_auth_token') {
        return value;
      }
    }
  }
  return null;
};

/**
 * 获取认证token（优先从内存，降级到cookie）
 * @returns 认证token，如果不存在则返回null
 */
export const getAuthToken = (): string | null => {
  // 优先从内存状态中获取
  const tokenFromMemory = useUserStore.getState().getToken();
  if (tokenFromMemory) {
    return tokenFromMemory;
  }

  // 降级到cookie
  const tokenFromCookie = getAuthTokenFromCookie();
  if (tokenFromCookie) {
    // 如果从cookie中获取到了token，同步到内存状态
    useUserStore.getState().setToken(tokenFromCookie);
    return tokenFromCookie;
  }

  return null;
};

/**
 * 设置认证token（同时设置到内存和cookie）
 * @param token 认证token（可以是 "Bearer xxx" 或 "xxx" 格式）
 */
export const setAuthToken = (token: string): void => {
  const tokenValue = token.replace('Bearer ', '');

  // 设置到内存状态
  useUserStore.getState().setToken(tokenValue);

  // 设置到cookie
  if (typeof document !== 'undefined') {
    document.cookie = `ide_auth_token=${tokenValue}; path=/; max-age=${7 * 24 * 60 * 60}`; // 7天过期
  }
};

/**
 * 删除认证cookie
 * 通过设置过期时间为过去的时间来删除cookie
 */
export const removeAuthTokenFromCookie = (): void => {
  if (typeof document !== 'undefined') {
    // 设置cookie的过期时间为过去的时间，从而删除cookie
    document.cookie = 'ide_auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  }
};

/**
 * 删除认证token（同时删除内存和cookie）
 */
export const removeAuthToken = (): void => {
  // 删除内存状态
  useUserStore.getState().setToken(null);

  // 删除cookie
  removeAuthTokenFromCookie();
};

export const client = axios.create({
  baseURL: typeof AXIOS_BASE !== 'undefined' ? AXIOS_BASE : undefined,
  headers:
    typeof AXIOS_HEADERS !== 'undefined' ? (JSON.parse(AXIOS_HEADERS) as AxiosHeaders) : undefined,
  withCredentials: true, // 允许携带 cookie
});

// 根据域名使用不同的拦截器
if (isInternalDomain()) {
  // ==================== 内部域名拦截器 ====================
  // 请求拦截器：使用 SSO cookie 认证
  client.interceptors.request.use(
    (config) => {
      // 设置 accept-language 为英文，确保接口返回英文内容
      config.headers = config.headers || {};
      config.headers['accept-language'] = 'en-US,en;q=0.9';

      // 从 store 获取 username 并添加到请求头
      const username = useUserStore.getState().user?.name;
      if (username) {
        config.headers['kwaipilot-username'] = username;
      }

      // 在开发环境下，从环境变量读取 Cookie 并添加到请求头
      if (import.meta.env.DEV && import.meta.env.VITE_PROXY_COOKIE) {
        config.headers['Cookie'] = import.meta.env.VITE_PROXY_COOKIE;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  // 响应拦截器：处理内部 SSO 错误
  client.interceptors.response.use(
    (response) => {
      const data = response.data;
      if (data.status === 401) {
        return Promise.reject(data);
      }
      return response.data;
    },
    (error) => {
      // 如果是401错误，跳转到内部 SSO 登录
      // 但是对于 /api/auth/sso/user 这个特殊的认证检查接口，不自动跳转
      // 避免在 UserProvider 中反复横跳
      if (error.response?.status === 401 && !error.config?.url?.includes('/api/auth/sso/user')) {
        // 内部域名直接跳转到 SSO 登录页
        window.location.href = generateInternalSSOUrl();
      }
      return Promise.reject(error);
    },
  );
} else {
  // ==================== 外部域名拦截器（原逻辑保持不变）====================
  // 请求拦截器：自动添加认证token到请求头
  client.interceptors.request.use(
    (config) => {
      // 设置 accept-language 为英文，确保接口返回英文内容
      config.headers = config.headers || {};
      config.headers['accept-language'] = 'en-US,en;q=0.9';

      const token = getAuthToken();
      if (token) {
        // 如果请求头中没有Authorization，则添加Bearer token
        if (!config.headers?.Authorization) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  client.interceptors.response.use(
    (response) => {
      // 检查响应头中是否有新的token
      const newToken = response.headers['x-new-token'];
      if (newToken && typeof document !== 'undefined') {
        // 更新token（同时更新内存和cookie）
        setAuthToken(newToken);
      }

      return response.data;
    },
    (error) => {
      // 如果是401错误，清除认证cookie并跳转到登录页
      if (error.response?.status === 401) {
        removeAuthToken();
        // 只有在不是登录相关页面时才跳转
        if (
          typeof window !== 'undefined' &&
          !window.location.pathname.includes('/login') &&
          !window.location.pathname.includes('/register') &&
          !window.location.pathname.includes('/forgot-password')
        ) {
          window.location.href = '/login';
        }
      }
      return Promise.reject(error);
    },
  );
}

/**
 * 使用示例：
 *
 * // 内部域名（如 xxx.kuaishou.com）：
 * // - 自动使用浏览器的 SSO cookie 进行认证
 * // - 不需要手动管理 token
 *
 * // 外部域名：
 * // - 使用 ide_auth_token cookie 进行认证
 * // - 自动添加 Bearer token 到请求头
 *
 * // 获取用户信息 - 会根据域名自动选择认证方式
 * const getUserInfo = async () => {
 *   const response = await client.get('/api/auth/oauth/user');
 *   return response.data;
 * };
 *
 * // 发送带认证的POST请求
 * const createResource = async (data: any) => {
 *   const response = await client.post('/api/resource', data);
 *   return response.data;
 * };
 */